export default class RangePriceInputs {
    static init(): void {
        this.dualRangeInit();
    }

    static dualRangeInit(): void {
        // Kontrola existence kontejneru před inicializací
        const priceContainer = document.querySelector('.js-dual-range-slider-init');
        if (!priceContainer) {
            //console.warn('DualRangeSlider: Kontejner .js-dual-range-slider-init nebyl nalezen na této stránce');
            return;
        }

        class DualRangeSlider {
            private container: HTMLElement;
            private sliderTrack!: HTMLElement;
            private sliderRange!: HTMLElement;
            private minThumb!: HTMLElement;
            private maxThumb!: HTMLElement;
            private minInput!: HTMLInputElement;
            private maxInput!: HTMLInputElement;

            private min!: number;
            private max!: number;
            private step!: number;
            private minValue!: number;
            private maxValue!: number;
            private currency!: string;

            private isDragging: boolean = false;
            private activeThumb: ('min' | 'max') | null = null;

            constructor(containerSelector: string) {
                const container = document.querySelector(containerSelector);
                if (!container) {
                    throw new Error(`Container ${containerSelector} nebyl nalezen`);
                }
                this.container = container as HTMLElement;

                this.isDragging = false;
                this.activeThumb = null;

                this.initElements();
                this.loadConfiguration();
                this.bindEvents();
                this.updateSlider();
            }

            private initElements(): void {
                const sliderTrack = this.container.querySelector('.slider-track');
                const sliderRange = this.container.querySelector('#sliderRange');
                const minThumb = this.container.querySelector('#minThumb');
                const maxThumb = this.container.querySelector('#maxThumb');
                const minInput = this.container.querySelector('#minInput');
                const maxInput = this.container.querySelector('#maxInput');

                if (!sliderTrack || !minThumb || !maxThumb || !minInput || !maxInput || !sliderRange) {
                    throw new Error('Požadované DOM elementy nebyly nalezeny');
                }

                this.sliderTrack = sliderTrack as HTMLElement;
                this.sliderRange = sliderRange as HTMLElement;
                this.minThumb = minThumb as HTMLElement;
                this.maxThumb = maxThumb as HTMLElement;
                this.minInput = minInput as HTMLInputElement;
                this.maxInput = maxInput as HTMLInputElement;
            }

            private loadConfiguration(): void {
                // Načtení konfigurace z HTML atributů inputů
                this.min = Math.min(parseInt(this.minInput.min) || 0, parseInt(this.maxInput.min) || 0);
                this.max = Math.max(parseInt(this.minInput.max) || 100000, parseInt(this.maxInput.max) || 100000);
                this.step = parseInt(this.minInput.step) || parseInt(this.maxInput.step) || 1;

                // Načtení počátečních hodnot z value atributů
                this.minValue = this.parseInputValue(this.minInput.value) || this.min;
                this.maxValue = this.parseInputValue(this.maxInput.value) || this.max;

                // Zajistit platný rozsah
                this.minValue = Math.max(this.min, Math.min(this.minValue, this.maxValue - this.step));
                this.maxValue = Math.max(this.minValue + this.step, Math.min(this.maxValue, this.max));

                // Detekce měny z value
                this.currency =
                    this.extractCurrency(this.minInput.value) || this.extractCurrency(this.maxInput.value) || 'Kč';
            }

            private extractCurrency(value: string): string | null {
                const match = value.match(/[^\d\s]+/);
                return match ? match[0] : null;
            }

            private bindEvents(): void {
                // Mouse events
                this.minThumb.addEventListener('mousedown', (e) => this.startDrag(e, 'min'));
                this.maxThumb.addEventListener('mousedown', (e) => this.startDrag(e, 'max'));

                // Touch events
                this.minThumb.addEventListener('touchstart', (e) => this.startDrag(e, 'min'), { passive: false });
                this.maxThumb.addEventListener('touchstart', (e) => this.startDrag(e, 'max'), { passive: false });

                // Global events
                document.addEventListener('mousemove', (e) => this.drag(e));
                document.addEventListener('mouseup', () => this.endDrag());
                document.addEventListener('touchmove', (e) => this.drag(e), { passive: false });
                document.addEventListener('touchend', () => this.endDrag());

                // Input events
                this.minInput.addEventListener('input', () => this.handleInputChange('min'));
                this.maxInput.addEventListener('input', () => this.handleInputChange('max'));
                this.minInput.addEventListener('blur', () => this.validateInput('min'));
                this.maxInput.addEventListener('blur', () => this.validateInput('max'));

                // Track click
                this.sliderTrack.addEventListener('click', (e) => this.handleTrackClick(e));

                // Prevent text selection
                document.addEventListener('selectstart', (e) => {
                    if (this.isDragging) e.preventDefault();
                });
            }

            private startDrag(e: MouseEvent | TouchEvent, thumbType: 'min' | 'max'): void {
                e.preventDefault();
                e.stopPropagation();

                this.isDragging = true;
                this.activeThumb = thumbType;

                const thumb = thumbType === 'min' ? this.minThumb : this.maxThumb;
                thumb.classList.add('active');

                document.body.style.userSelect = 'none';
                document.body.style.cursor = 'grabbing';
            }

            private drag(e: MouseEvent | TouchEvent): void {
                if (!this.isDragging || !this.activeThumb) return;

                e.preventDefault();

                const clientX = this.getClientX(e);
                const newValue = this.calculateValueFromPosition(clientX);

                if (this.activeThumb === 'min') {
                    this.minValue = Math.min(newValue, this.maxValue - this.step);
                    this.minValue = Math.max(this.minValue, this.min);
                } else {
                    this.maxValue = Math.max(newValue, this.minValue + this.step);
                    this.maxValue = Math.min(this.maxValue, this.max);
                }

                this.updateSlider();
            }

            private endDrag(): void {
                if (!this.isDragging) return;

                this.isDragging = false;
                this.activeThumb = null;

                this.minThumb.classList.remove('active');
                this.maxThumb.classList.remove('active');

                document.body.style.userSelect = '';
                document.body.style.cursor = '';

                // Dispatch change event for both inputs after dragging ends
                this.minInput.dispatchEvent(new Event('change', { bubbles: true }));
                this.maxInput.dispatchEvent(new Event('change', { bubbles: true }));
            }

            private getClientX(e: MouseEvent | TouchEvent): number {
                return e.type.includes('touch') ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
            }

            private calculateValueFromPosition(clientX: number): number {
                const rect = this.sliderTrack.getBoundingClientRect();
                const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
                return Math.round(this.min + percentage * (this.max - this.min));
            }

            private handleInputChange(type: 'min' | 'max'): void {
                const input = type === 'min' ? this.minInput : this.maxInput;
                const cleanValue = this.parseInputValue(input.value);

                if (cleanValue === null) return;

                if (type === 'min') {
                    this.minValue = Math.max(this.min, Math.min(cleanValue, this.maxValue - this.step));
                } else {
                    this.maxValue = Math.max(this.minValue + this.step, Math.min(cleanValue, this.max));
                }

                this.updateSlider();
            }

            private validateInput(type: 'min' | 'max'): void {
                const input = type === 'min' ? this.minInput : this.maxInput;
                const cleanValue = this.parseInputValue(input.value);

                if (cleanValue === null || cleanValue < this.min || cleanValue > this.max) {
                    this.updateInputs();
                }
            }

            private parseInputValue(value: string): number | null {
                // Extrakce čísla z textu (např. "1 500 Kč" -> 1500)
                const cleanValue = value.replace(/[^\d]/g, '');
                const numValue = parseInt(cleanValue);

                return isNaN(numValue) ? null : numValue;
            }

            private formatValueWithCurrency(num: number): string {
                // Formátování s mezerami a měnou
                const formatted = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                return `${formatted} ${this.currency}`;
            }

            private handleTrackClick(e: MouseEvent): void {
                if (this.isDragging) return;

                const clientX = this.getClientX(e);
                const value = this.calculateValueFromPosition(clientX);

                const minDistance = Math.abs(value - this.minValue);
                const maxDistance = Math.abs(value - this.maxValue);

                if (minDistance < maxDistance) {
                    this.minValue = Math.min(value, this.maxValue - this.step);
                    this.minValue = Math.max(this.minValue, this.min);
                } else {
                    this.maxValue = Math.max(value, this.minValue + this.step);
                    this.maxValue = Math.min(this.maxValue, this.max);
                }

                this.updateSlider();
            }

            private updateSlider(): void {
                this.updateThumbs();
                this.updateRange();
                this.updateInputs();
            }

            private updateThumbs(): void {
                const minPercent = ((this.minValue - this.min) / (this.max - this.min)) * 100;
                const maxPercent = ((this.maxValue - this.min) / (this.max - this.min)) * 100;

                this.minThumb.style.left = `calc(${minPercent}% - 12px)`;
                this.maxThumb.style.left = `calc(${maxPercent}% - 12px)`;
            }

            private updateRange(): void {
                const minPercent = ((this.minValue - this.min) / (this.max - this.min)) * 100;
                const maxPercent = ((this.maxValue - this.min) / (this.max - this.min)) * 100;

                this.sliderRange.style.left = `${minPercent}%`;
                this.sliderRange.style.width = `${maxPercent - minPercent}%`;
            }

            private updateInputs(): void {
                this.minInput.value = this.formatValueWithCurrency(this.minValue);
                this.maxInput.value = this.formatValueWithCurrency(this.maxValue);
            }

            // Public API
            public getValues(): { min: number; max: number; currency: string } {
                return {
                    min: this.minValue,
                    max: this.maxValue,
                    currency: this.currency,
                };
            }

            public setValues(min?: number, max?: number): void {
                if (min !== undefined) {
                    this.minValue = Math.max(this.min, Math.min(min, this.maxValue - this.step));
                }
                if (max !== undefined) {
                    this.maxValue = Math.max(this.minValue + this.step, Math.min(max, this.max));
                }
                this.updateSlider();
            }
        }

        // Inicializace pouze pokud kontejner existuje
        const slider = new DualRangeSlider('.js-dual-range-slider-init');

        // Event listener pro testování
        // document.addEventListener('input', () => {
        //     const values = slider.getValues();
        //     console.log(`Aktuální rozsah: ${values.min} - ${values.max} ${values.currency}`);
        // });

        // Global access
        (window as any).DualRangeSlider = DualRangeSlider;
    }
}
